import { useEffect, useState, useMemo } from 'preact/hooks';
import { type PageTheme, type ThemeFont, type AppliedFont, getFontFamily } from './theme-panel';
import { getFontImport } from './theme-panel/utils';
import { unique } from 'shared/utils';
import { BUILT_IN_FONTS } from './theme-panel/constants';

interface ThemeStylesProps {
  theme: PageTheme;
}

/**
 * Simple font loading detection hook.
 *
 * This is intentionally simple - we wait 300ms and assume fonts are loaded.
 * More sophisticated approaches (Font Loading API, FontFaceObserver) add complexity
 * and have browser compatibility concerns. The 300ms timeout provides a reasonable
 * balance between UX (avoiding flash of unstyled text) and simplicity.
 *
 * If fonts take longer to load, they'll appear with a slight delay, which is acceptable.
 */
export function useThemeFonts() {
  const [fontsLoaded, setFontsLoaded] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setFontsLoaded(true), 300);
    return () => clearTimeout(timeout);
  }, []);

  return fontsLoaded;
}

const ELEMENT_SELECTORS = {
  h1: ['.minidoc-content h1', 'h1'],
  h2: ['.minidoc-content h2', 'h2'],
  h3: ['.minidoc-content h3', 'h3'],
  content: ['.minidoc-content p', 'p', 'div', 'span', 'li', 'td'],
};

function generateElementStyles(
  elementType: string,
  appliedFont: AppliedFont,
  fonts: ThemeFont[],
): string {
  const selectors = ELEMENT_SELECTORS[elementType as keyof typeof ELEMENT_SELECTORS];
  if (!selectors) {
    return '';
  }

  const selector = selectors
    .map((s) => `.sales-page[data-theme-styles] ${s}:not(.editor-chrome)`)
    .join(', ');
  const font = fonts.find((f) => f.id === appliedFont.fontId);
  const fontFamily = font ? getFontFamily(font) : undefined;

  const properties: string[] = [];

  if (fontFamily) {
    properties.push(`font-family: ${fontFamily} !important`);
  }
  if (appliedFont.size) {
    properties.push(`font-size: ${appliedFont.size}px !important`);
  }
  if (appliedFont.weight) {
    properties.push(`font-weight: ${appliedFont.weight} !important`);
  }
  if (appliedFont.leading) {
    properties.push(`line-height: ${appliedFont.leading} !important`);
  }
  if (appliedFont.marginBottom) {
    properties.push(`margin-bottom: ${appliedFont.marginBottom}px !important`);
  }

  if (properties.length === 0) {
    return '';
  }

  return `${selector} {
    ${properties.join(';\n    ')};
  }`;
}
function generateCTAStyles(theme: PageTheme): string {
  if (!theme.cta) {
    return '';
  }

  const properties: string[] = [];
  const darkSectionProperties: string[] = [];

  if (theme.cta.bgcolor) {
    properties.push(`background-color: ${theme.cta.bgcolor}`);
    // Invert colors for dark sections - light button on dark background
    darkSectionProperties.push(`background-color: ${theme.cta.fgcolor || '#ffffff'}`);
  }

  if (theme.cta.fgcolor) {
    properties.push(`color: ${theme.cta.fgcolor}`);
    // Invert colors for dark sections - dark text on light button
    darkSectionProperties.push(`color: ${theme.cta.bgcolor || '#1e293b'}`);
  }

  if (theme.cta.borderRadius) {
    // Extract the actual CSS value from the Tailwind class
    const borderRadiusValue = getBorderRadiusValue(theme.cta.borderRadius);
    if (borderRadiusValue) {
      properties.push(`border-radius: ${borderRadiusValue}`);
      darkSectionProperties.push(`border-radius: ${borderRadiusValue}`);
    }
  }

  if (properties.length === 0) {
    return '';
  }

  const selector = '.sales-page[data-theme-styles] .call-to-action';
  const darkSelector = '.sales-page[data-theme-styles] .sales-page-dark .call-to-action';

  let css = `${selector} {
    ${properties.join(';\n    ')};
  }`;

  if (darkSectionProperties.length > 0) {
    css += `\n\n${darkSelector} {
    ${darkSectionProperties.join(';\n    ')};
  }`;
  }

  return css;
}

function useThemeStyles(theme: PageTheme): string {
  return useMemo(() => {
    const ctaStyles = generateCTAStyles(theme);

    if (!theme.appliedFonts) {
      return ctaStyles;
    }

    // Combine built-in fonts with custom fonts from theme
    const allFonts = [
      ...BUILT_IN_FONTS,
      ...(theme.fonts || []).filter((f) => !BUILT_IN_FONTS.some((b) => b.id === f.id)),
    ];

    const usedFontIds = unique(
      Object.values(theme.appliedFonts)
        .map((x) => x?.fontId)
        .filter(Boolean),
    );
    const fontImports = usedFontIds
      .map((id) => allFonts.find((x) => x.id === id))
      .filter((font): font is ThemeFont => Boolean(font))
      .map(getFontImport)
      .join('\n');

    const elementStyles = Object.entries(theme.appliedFonts)
      .map(([elementType, appliedFont]) => {
        if (!appliedFont) {
          return '';
        }
        return generateElementStyles(elementType, appliedFont, allFonts);
      })
      .filter(Boolean);

    return [fontImports, ...elementStyles, ctaStyles].filter(Boolean).join('\n');
  }, [theme]);
}

export function getBorderRadiusValue(borderRadius: string): string | undefined {
  switch (borderRadius) {
    case 'none':
      return '0';
    case 'small':
      return '0.125rem';
    case 'medium':
      return '0.5rem';
    case 'large':
      return '1rem';
    case 'full':
      return '9999px';
    default:
      return undefined;
  }
}

/**
 * ThemeStyles component that generates and injects CSS styles for theme fonts.
 *
 * This component:
 * - Generates @import statements for custom fonts
 * - Creates CSS rules for h1, h2, h3, and content elements based on theme settings
 * - Uses CSS selectors that exclude editor UI with 'editor-chrome' class from theme styling
 *
 * Note: The 'editor-chrome' class is reserved for editor UI elements to prevent
 * theme fonts from affecting the editing interface. Currently not actively used
 * but kept for future editor UI styling needs.
 */
export function ThemeStyles({ theme }: ThemeStylesProps) {
  const allStyles = useThemeStyles(theme);

  return <style dangerouslySetInnerHTML={{ __html: allStyles }} data-theme-styles />;
}
